<template>
  <ProfileNavBar />
  <div class="content">
    <!---------------- Headers ------------>
    <div class="title">
      <div class="left-part">
        <b class="header">{{
          formatLabel(responseData?.header?.company_name)
        }}</b>
        <div class="caption">
          <span class="most-recent-financial">Address: </span>
          <b>{{
            formatLabel(responseData?.header?.address) || "XXXX, XXXX, XXXX,"
          }}</b>
        </div>
      </div>
      <div class="right-part">
        <div class="buttons-group"></div>
        <div class="caption2">
          <p class="most-recent-financial">
            Most recent financial data:
            {{ responseData?.latestFinancialsDate || "20xx xx xx" }}
          </p>
          <p class="most-recent-financial">Available in: English</p>
        </div>
      </div>
    </div>
    <!------------Description ------------->
    <div class="tile">
      <div class="card-base-header">
        <div class="caption3">
          <div class="caption4">
            <div
              class="lock-content"
              v-if="!responseData?.summary?.aboutTheCompany"
              style="display: flex; flex-direction: row; gap: 14px"
            >
              <div
                class="section-icons"
                v-html="icons?.lock"
                title="This content is locked. Buy now to access."
              ></div>
              <p style="margin: 7px">
                This content is locked. Buy now to access.
              </p>
            </div>
            <span v-else> {{ responseData?.summary?.aboutTheCompany }}</span>
          </div>
        </div>
      </div>
    </div>
    <!-- ---------Charts & Maps-------------- -->
    <div class="chart-div">
      <!-- Map Section -->
      <div class="report-section-wrapper finance-section-wrapper">
        <div class="report-section">
          <div>
            <div class="section-header">
              <h2>Headquarters</h2>
            </div>
            <div class="headquaters-container">
              <p class="headquaters-heading">
                {{ addressToShow || responseData?.header?.address }}
              </p>
            </div>
          </div>
          <!-- If locked show lock Icon -->
          <div
            class="table-container"
            style="height: -webkit-fill-available"
            v-if="!responseData?.header?.address?.length && !addressToShow"
          >
            <div class="graph-blur-overlay">
              <div class="lock-content">
                <div v-html="icons?.lock" class="lock-icon"></div>
                <p>This content is locked. Buy now to access.</p>
              </div>
            </div>
          </div>
          <!-- If not locked show map -->
          <div class="map-container" ref="mapContainer"></div>
        </div>
      </div>
      <!-- Chart Section -->
      <div class="report-section-wrapper finance-section-wrapper">
        <div class="report-section">
          <div>
            <div class="section-header">
              <h2>Company Performance</h2>
            </div>
            <div
              class="table-container"
              style="height: 400px"
              v-if="!responseData?.finance?.chartData?.length"
            >
              <span class="not-found-text" style="padding-top: 20px">
                Financial information for this entity is not publicly disclosed
                or accessible through verified sources at the time of reporting.
                This may be due to jurisdiction-specific disclosure norms, the
                company’s legal structure, regulatory exemptions, or non-filing
                status. As such, financials have not been included in this
                report. All efforts have been made to source available data
                through reliable public and proprietary channels.</span
              >
            </div>
            <div class="table-container" style="height: 400px" v-else>
              <Bar :data="revenueChartData" :options="barChartOptions" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-------------Company Info & Shareholders  -->
    <div class="chart-div">
      <!-- Company Info -->
      <div class="report-section-wrapper finance-section-wrapper">
        <div class="report-section">
          <div>
            <div class="section-header">
              <h2>Company Information</h2>
            </div>
          </div>

          <div class="table">
            <div class="rows" style="border-top: 1px solid #e2e8f0">
              <div class="cells">
                <div class="incorporation-date">Incorporation Date</div>
              </div>
              <div class="cells1">
                <div class="table-value">
                  <div
                    v-if="
                      !responseData?.company?.keyInformation?.incorporation_date
                    "
                    class="section-icons"
                    v-html="icons?.lock"
                    title="This content is locked. Buy now to access."
                  ></div>
                  <span v-else>
                    {{
                      responseData?.company?.keyInformation?.incorporation_date
                    }}</span
                  >
                </div>
              </div>
            </div>
            <div class="rows">
              <div class="cells">
                <div class="incorporation-date">Company Id</div>
              </div>
              <div class="cells1">
                <div class="table-value">
                  <div
                    v-if="!responseData?.company?.keyInformation?.companyId"
                    class="section-icons"
                    v-html="icons?.lock"
                    title="This content is locked. Buy now to access."
                  ></div>
                  <span v-else>
                    {{ responseData?.company?.keyInformation?.companyId }}</span
                  >
                </div>
              </div>
            </div>
            <div class="rows">
              <div class="cells">
                <div class="incorporation-date">Company Type</div>
              </div>
              <div class="cells1">
                <div class="table-value">
                  <div
                    v-if="!responseData?.company?.keyInformation?.typeOfEntity"
                    class="section-icons"
                    v-html="icons?.lock"
                    title="This content is locked. Buy now to access."
                  ></div>
                  <span v-else>
                    {{
                      responseData?.company?.keyInformation?.typeOfEntity
                    }}</span
                  >
                </div>
              </div>
            </div>
          </div>
          <div
            class="graph-blur-overlay"
            v-if="!responseData?.company?.keyInformation"
            style="top: 40px"
          >
            <div class="lock-content">
              <div v-html="icons?.lock" class="lock-icon"></div>
              <p>This content is locked. Buy now to access.</p>
            </div>
          </div>
        </div>
      </div>
      <!-- Shareholders -->
      <div class="report-section-wrapper finance-section-wrapper">
        <div class="report-section">
          <div>
            <div class="section-header">
              <h2>Shareholders</h2>
            </div>
          </div>
          <div class="table">
            <div
              class="rows"
              :style="index == 0 ? 'border-top: 1px solid #e2e8f0' : ''"
              v-for="(shareholder, index) in data?.shareholders"
              :key="index"
            >
              <div class="cells" v-for="(value, key) in shareholder" :key="key">
                <div class="total-shares">{{ value }}</div>
              </div>
              <div class="cells1">
                <div class="purchase-report-to-container">
                  <b
                    class="purchase"
                    style="cursor: pointer"
                    @click="navigateOnPackageModule"
                    >Purchase</b
                  >
                  <span class="report-to-view"> report to view</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-------------Related Parties & Ratios------------>
    <div class="chart-div">
      <!-- Related Parties -->
      <div class="report-section-wrapper finance-section-wrapper">
        <div class="report-section">
          <div>
            <div class="section-header">
              <h2>Related Parties</h2>
            </div>
          </div>
          <!-- Table -->
          <div
            class="table-container"
            v-if="responseData?.related_parties?.length"
          >
            <table class="peers-table">
              <thead>
                <tr>
                  <th
                    v-for="key in columnKeys(responseData?.related_parties)"
                    :key="key"
                  >
                    {{ formatColumnName(key) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item, index) in responseData?.related_parties"
                  :key="index"
                >
                  <td
                    v-for="key in columnKeys(responseData?.related_parties)"
                    :key="`${index}-${key}`"
                  >
                    <div
                      v-if="key == 'name' || item[key] == ''"
                      class="section-icons"
                      v-html="icons?.lock"
                      title="This content is locked. Buy now to access."
                    ></div>
                    <span v-else> {{ item[key] }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <!-- If data is locked show dummy data -->
          <div class="table-container" v-else>
            <span class="not-found-text">
              No records of branches, subsidiaries, sister concerns, or
              associated entities could be identified for this company based on
              currently accessible public, proprietary, and partner data sources
              available to us. This does not definitively confirm the absence of
              related parties. Data availability may vary by jurisdiction, and
              certain relationships may not be publicly disclosed or may exist
              under alternate legal or trade names. Users are advised to conduct
              further investigation where enhanced verification is
              required.</span
            >
          </div>
          <!-- <template v-else>
            <div class="table-container">
              <table class="peers-table">
                <thead>
                  <tr>
                    <th>kjhguy</th>
                    <th>kjhguy</th>
                    <th>kjhguy</th>
                    <th>kjhguy</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="n in responseData?.finance_ratios?.length || 5"
                    :key="n"
                  >
                    <td><span> kjjjkjkjk</span></td>
                    <td><span> kjjjkjkjk</span></td>
                    <td><span> kjjjkjkjk</span></td>
                    <td><span> kjjjkjkjk</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="graph-blur-overlay" style="top: 40px">
              <div class="lock-content">
                <div v-html="icons?.lock" class="lock-icon"></div>
                <p>This content is locked. Buy now to access.</p>
              </div>
            </div>
          </template> -->
        </div>
      </div>
      <!-- Ratios -->
      <div class="report-section-wrapper finance-section-wrapper">
        <div class="report-section">
          <div class="section-header-wrapper">
            <div class="section-header">
              <h2>Financials</h2>
            </div>
          </div>
          <div class="finace-section-wrapper-1">
            <div class="finance-section-content">
              <!-- Ratios Table -->
              <div
                class="table-container"
                v-if="responseData?.finance_ratios?.length"
              >
                <div class="table-wrapper">
                  <table class="peers-table">
                    <thead>
                      <tr>
                        <th
                          v-for="(header, index) in tableHeaders"
                          :key="'header-' + index"
                        >
                          {{ header }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <template
                        v-for="(row, rowIndex) in responseData?.finance_ratios"
                        :key="'row-' + rowIndex"
                      >
                        <tr>
                          <!-- Name Column Always Visible -->
                          <td>{{ row?.name }}</td>

                          <!-- Other Columns: Conditionally Blur -->
                          <td
                            v-for="(header, colIndex) in tableHeaders.slice(1)"
                            :key="'col-' + colIndex"
                            class="value-cell"
                            style="text-align: center"
                          >
                            <div v-if="rowIndex < 3">
                              <div
                                v-if="
                                  row[header]?.value == '' ||
                                  row[header]?.value == null
                                "
                                class="section-icons ratio"
                                v-html="icons?.lock"
                                title="This content is locked. Buy now to access."
                              ></div>
                              <span v-else> {{ row[header]?.value }}</span>
                              <span>
                                <svg
                                  width="18"
                                  height="18"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  :style="
                                    !row[header]?.indicator
                                      ? 'visibility: hidden'
                                      : ''
                                  "
                                >
                                  <path
                                    :d="
                                      row[header]?.indicator === 'positive' ||
                                      !row[header]?.indicator
                                        ? 'M7 14L12 9L17 14H7Z'
                                        : 'M7 10L12 15L17 10H7Z'
                                    "
                                    :fill="
                                      row[header]?.indicator === 'positive'
                                        ? '#27BE69'
                                        : row[header]?.indicator === 'negative'
                                        ? '#FF0000'
                                        : '#A0A0A0'
                                    "
                                  />
                                </svg>
                              </span>
                            </div>
                            <!-- Blurred -->
                            <span v-else class="blur-target">
                              ****
                              <span>
                                <svg
                                  width="18"
                                  height="18"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M7 14L12 9L17 14H7Z"
                                    fill="#27BE69"
                                  />
                                </svg>
                              </span>
                            </span>
                          </td>
                        </tr>
                      </template>
                    </tbody>
                  </table>

                  <!-- Blur & Lock Overlay -->
                  <div class="blur-overlay">
                    <div class="lock-content">
                      <div v-html="icons?.lock" class="lock-icon"></div>
                      <p>This content is locked. Buy now to access.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="table-container" v-else>
                <span class="not-found-text">
                Financial information for this entity is not publicly disclosed
                or accessible through verified sources at the time of reporting.
                This may be due to jurisdiction-specific disclosure norms, the
                company’s legal structure, regulatory exemptions, or non-filing
                status. As such, financials have not been included in this
                report. All efforts have been made to source available data
                through reliable public and proprietary channels.</span
              >
              </div>
              <!-- <template v-else>
                <div class="table-container">
                  <table class="peers-table">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Year 1</th>
                        <th>Year 2</th>
                        <th>Year 3</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="n in 5" :key="n">
                        <td><span>****</span></td>
                        <td><span> ****</span></td>
                        <td><span> ****</span></td>
                        <td><span> ****</span></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div class="graph-blur-overlay" style="top: 40px">
                  <div class="lock-content">
                    <div v-html="icons?.lock" class="lock-icon"></div>
                    <p>This content is locked. Buy now to access.</p>
                  </div>
                </div>
              </template> -->
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Footer -->
    <div class="tile">
      <div class="footer-heading">
        <div class="titleWrapper">
        <div class="title">Comprehensive Report</div>
        <p class="as-of">As of: {{ responseData?.header?.lastUpdated }}</p>
      </div>
        <button class="button2" @click="navigateOnPackageModule">
          <div
            class="section-icons"
            v-html="icons?.cart"
            style="margin-right: 7px"
          ></div>
          Buy Now
        </button>
      </div>
      <div class="content1">
        <div class="number">
          USD {{ responseData?.priceMap?.NO_UPDATE?.promotion_purchase_usd }}
        </div>
        <div class="footer-caption">
          <div class="percent">
            Get access to our latest comprehensive reports with in-depth
            analysis and insights.
          </div>
        </div>
      </div>
    </div>
  </div>
  <Toastify ref="toastContainer" />
  <div class="loader-wrapper" v-if="isLoading">
    <LoaderAnimation />
  </div>
  <ProfilePageFooter isProfilePage="true"></ProfilePageFooter>
</template>

<script>
/* global google */
import { Bar } from "vue-chartjs";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

import { getCompanyProfileDetails } from "@/api/APIs";
import router from "@/router";
import Toastify from "@/components/Toastify.vue";
import LoaderAnimation from "@/assets/Animation/LoaderAnimation.vue";
import CompanyProfileIcons from "@/assets/DigitalReport/companyProfileIcons";
import { useHead } from "@unhead/vue";
import { useRoute } from "vue-router";
import ProfilePageFooter from "@/components/HomePage/Footer/ProfilePageFooter.vue";
import ProfileNavBar from "@/components/ProfileNavBar.vue";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default {
  name: "CompanyProfile",
  setup() {
    const route = useRoute();
    const slug =
      route.params?.slug ||
      route.path.split("/company-profile/")[1] ||
      "Company Profile";
    const fallbackTitle = `${slug.replace(/-/g, " ")} | Company Research Report`;
    useHead({
      title: fallbackTitle,
      meta: [
        {
          name: "description",
          content: `${fallbackTitle}: Explore due diligence insights and risk analysis.`,
        },
        {
          property: "og:title",
          content: fallbackTitle,
        },
        {
          property: "og:url",
          content: typeof window !== "undefined" ? window.location.href : "",
        },
      ],
    });
  },
  components: {
    Bar,
    Toastify,
    LoaderAnimation,
    ProfilePageFooter,
    ProfileNavBar,
  },
  data() {
    return {
      map: null,
      data: {
        shareholders: [
          { label: "Shareholder 1" },
          { label: "Shareholder 2" },
          { label: "Total Shares" },
        ],
      },
      responseData: null,
      addressToShow: "",
      isLoading: false,
      barChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "bottom",
          },
          tooltip: false,
        },
        scales: {
          x: {
            grid: {
              display: false, // Hide x-axis grid lines
              drawBorder: false,
            },
            ticks: {
              padding: 10,
            },
          },
          y: {
            beginAtZero: true,
            display: true, // Must be true to show the axis line
            grid: {
              display: false,
              drawBorder: true, // Show the Y-axis line
            },
            ticks: {
              padding: 10,
              callback: function (value) {
                return value === 0 ? "0" : "";
              },
            },
          },
        },
      },
    };
  },
  watch: {
    responseData: {
      deep: true,
      handler(newData) {
        if (newData?.company?.contactInformation?.addresses) {
          this.initMap();
        }
      },
    },
  },
  computed: {
    icons() {
      return CompanyProfileIcons;
    },
    revenueChartData() {
      const chartData = this.responseData?.finance?.chartData || [];
      return {
        labels: chartData.map((item) => item?.month),
        datasets: [
          {
            label: this.country === "China" ? "Operating Income" : "Revenue",
            data: chartData.map((item) => item?.value?.revenueChartData ?? 0),
            backgroundColor: "#613dc1",
            borderColor: "#613dc1",
            borderWidth: 1,
          },
          {
            label: "Net Profit",
            data: chartData.map((item) => item?.value?.netProfitChartData ?? 0),
            backgroundColor: "#f72585",
            borderColor: "#f72585",
            borderWidth: 1,
          },
        ],
      };
    },
    tableHeaders() {
      if (!this.responseData?.finance_ratios?.length) return [];

      // Collect all unique keys across rows (besides 'name')
      const allKeys = new Set(["name"]);
      this.responseData?.finance_ratios.forEach((row) => {
        Object.keys(row).forEach((key) => {
          if (key !== "name") {
            allKeys.add(key);
          }
        });
      });

      // Convert Set to Array
      return Array.from(allKeys);
    },
  },
  mounted() {
    this.getCompanyProfileData();
    this.initMap();
  },
  methods: {
    navigateOnPackageModule() {
      this.trackBuyNowClick(); // ✅ Send first
      setTimeout(() => {
        router.push(
          `/choose-package/${this.$route?.href?.split("/company-profile/")[1]}`
        );
      }, 200);
    },
    trackBuyNowClick() {
      if (typeof window.gtag === "function") {
        const route = this.$route;
        window.gtag("event", "buy_now_click", {
          event_category: "engagement",
          event_label: `${route.params.company} - ${route.params.country}`,
          company_name: route.params.company,
          country: route.params.country,
          page_path: route.fullPath,
          page_location: window.location.href,
          page_title: document.title,
          transport_type: "beacon", // ✅ this is key!
        });
      }
    },
    formatLabel(key) {
      return (
        key &&
        key
          ?.replace(/_/g, " ")
          ?.replace(/([a-z])([A-Z])/g, "$1 $2")
          ?.replace(/\b\w/g, (char) => char.toUpperCase())
      );
    },
    formatColumnName(key) {
      return key
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
    columnKeys(tableData) {
      if (!tableData || !Array.isArray(tableData) || tableData.length === 0) {
        return [];
      }

      // Collect all possible keys from the dataset
      const keys = new Set();
      tableData.forEach((item) => {
        Object.keys(item).forEach((key) => {
          if (item[key] !== undefined && item[key] !== "") {
            keys.add(key);
          }
        });
      });

      // Convert Set to Array
      let allKeys = Array.from(keys);

      // Filter out specific keys for PDF
      if (this.isPdf) {
        const excludedKeys = ["city", "cin"];
        allKeys = allKeys.filter(
          (key) =>
            !excludedKeys.some(
              (exKey) => exKey.toLowerCase() === key.toLowerCase()
            )
        );
      }

      // Prioritize certain columns to appear first
      const priorityOrder = [
        "name",
        "Name",
        "Director's Name",
        "company_name",
        "position",
        "Present Designation",
        "Designation",
        "address",
        "city_or_country",
        "city",
        "country",
        "class",
        "number_of_shares",
        "interest_percentage",
      ];

      allKeys.sort((a, b) => {
        const indexA = priorityOrder.indexOf(a);
        const indexB = priorityOrder.indexOf(b);

        if (indexA === -1 && indexB === -1) return a.localeCompare(b);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });

      return allKeys;
    },
    setMetaTags() {
      useHead({
        title: this.responseData?.header?.company_name,
        meta: [
          {
            name: "description",
            content:
              `${
                this.responseData?.header?.company_name || "This company"
              } is a ${
                this.responseData?.company?.keyInformation?.typeOfEntity ||
                "business entity"
              } ` +
                `located at ${
                  this.responseData?.header?.address || "an undisclosed address"
                }. ` +
                `${
                  this.responseData?.summary?.aboutTheCompanyForProfile ||
                  this.responseData?.summary?.aboutTheCompany ||
                  "More information will be added soon."
                }` || "",
          },
          {
            name: "keywords",
            content:
              `${this.responseData?.header?.company_name}, company due diligence, business reports, Credencedata, company reports, business analysis, company analysis, business intelligence, risk assessment` |
              "",
          },
          {
            property: "og:title",
            content: this.responseData?.header?.company_name || "Default Title",
          },
          {
            property: "og:description",
            content:
              `${
                this.responseData?.header?.company_name || "This company"
              } is a ${
                this.responseData?.company?.keyInformation?.typeOfEntity ||
                "business entity"
              } ` +
                `located at ${
                  this.responseData?.header?.address || "an undisclosed address"
                }. ` +
                `${
                  this.responseData?.summary?.aboutTheCompanyForProfile ||
                  this.responseData?.summary?.aboutTheCompany ||
                  "More information will be added soon."
                }` || "Default Descrition",
          },
          {
            property: "og:image",
            content: this.blogData?.bannerImage || "",
          },
          {
            property: "og:image:width",
            content: "1200",
          },
          {
            property: "og:image:height",
            content: "630",
          },
          {
            property: "og:url",
            content: window.location.href,
          },
        ],
      });
    },
    async getCompanyProfileData() {
      this.isLoading = true;
      const payload = {
        slug: this.$route?.href?.split("/company-profile/")[1],
      };
      try {
        let response = await getCompanyProfileDetails(payload);
        if (response) {
          this.responseData = response?.data;
          sessionStorage.setItem(
            "companyProfileData",
            JSON.stringify(response?.data)
          );
          this.setMetaTags();
        } else {
          this.$refs.toastContainer.addToast("", { type: "error" });
        }
      } catch (error) {
        if (error.response && error.response.status !== 403) {
          this.$refs.toastContainer.addToast("Company Not Found", {
            type: "error",
          });
        }
      } finally {
        this.isLoading = false;
      }
    },
    initMap() {
      if (typeof google !== "undefined" && google.maps) {
        const addresses =
          this.responseData?.company?.contactInformation?.addresses || [];
        let selectedAddress =
          addresses.find((addr) => addr?.addressType === "Registered Address")
            ?.address ||
          addresses.find((addr) => addr?.addressType === "Business Address")
            ?.address ||
          addresses[0]?.address ||
          " ";
        this.addressToShow = selectedAddress;
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ address: selectedAddress }, (results, status) => {
          if (status === "OK") {
            const location = results[0].geometry.location;
            this.map = new google.maps.Map(this.$refs.mapContainer, {
              center: location,
              zoom: 15,
              disableDefaultUI: true,
              draggable: true,
              zoomControl: false,
              streetViewControl: false,
              fullscreenControl: true,
              mapTypeControl: false,
            });
            new google.maps.Marker({
              position: location,
              map: this.map,
              title: "Business Location",
            });
          } else {
            console.error("Geocode was not successful: " + status);
          }
        });
      } else {
        console.error("Google Maps API is not loaded");
      }
    },
  },
};
</script>

<style scoped>
.not-found-text {
  color: #1c1c1c;
  font-family: Figtree, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
  text-align: justify;
  margin: 0;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.table-wrapper {
  position: relative;
  width: 100%;
}

.ratio {
  display: flex;
  width: 20px;
  left: 29px;
  position: relative;
  top: 11px;
}

/* Overlay to appear only over locked area */
.blur-overlay {
  position: absolute;
  top: 162px; /* same as 3 * 54px */
  left: calc(50% - 120px);
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8); /* more masking power */
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  pointer-events: none;
  /* border-radius: 4px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);  */
}

@supports not (
  (-webkit-backdrop-filter: blur(7px)) or (backdrop-filter: blur(7px))
) {
  .blur-overlay {
    background-color: rgba(255, 255, 255, 0.95); /* stronger fallback */
  }
  .graph-blur-overlay {
    background-color: rgba(255, 255, 255, 0.95); /* stronger fallback */
  }
}

.graph-blur-overlay {
  position: absolute;
  background-color: rgba(255, 255, 255, 0.8); /* more masking power */
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  pointer-events: none;
  inset: 0;
}

.lock-content {
  text-align: center;
  color: #d32f2f;
  font-weight: 500;
}

.lock-icon {
  font-size: 28px;
  margin-bottom: 8px;
}
/* ----------------- */
.report-section-wrapper {
  display: flex;
  padding: 20px 16px;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  border-radius: 12px;
  background: #fff;
  border: 1.5px solid #e0f5ff;
  color: #04162d;
  overflow: hidden; /* Prevent any potential horizontal scroll */
}

.report-section {
  position: relative;
}
/* Adjust padding for mobile */
@media screen and (max-width: 768px) {
  .report-section-wrapper {
    padding: 16px 12px;
    gap: 12px;
  }
}

.finance-section-wrapper {
  flex-direction: column;
}

.report-section {
  background-color: #fff;
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: -webkit-fill-available;
  position: relative;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.section-icons {
  display: flex;
  width: 20px;
}

h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  font-family: "Figtree";
  color: #142d4d;
  line-height: 1.3;
}

h3 {
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
}

.table-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 20px;
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: -ms-autohiding-scrollbar;
  position: relative;
  margin: 0 -20px;
  padding: 0 20px;
}

.peers-table {
  width: 100%;
  border-collapse: collapse;
  box-shadow: 0px 0px 12.7px #e0f5ff !important;
  table-layout: fixed; /* Helps with column width consistency */
}

/* Make table cells handle overflow */
.peers-table th,
.peers-table td {
  padding: 12px 8px;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px; /* Adjust based on your needs */
}

/* Ensure table headers stay visible on scroll */
.peers-table thead {
  position: sticky;
  top: 0;
  background-color: #f8fafc;
  z-index: 10;
}

/* Responsive adjustments */
@media screen and (max-width: 768px) {
  .table-container {
    margin: 0 -16px;
    padding: 0 16px;
  }

  .peers-table th,
  .peers-table td {
    padding: 8px 6px;
  }

  .section-header h2 {
    font-size: 16px;
  }
  .title {
    font-size: 16px;
  }
}

/* For very small screens */
@media screen and (max-width: 480px) {
  .peers-table th,
  .peers-table td {
    padding: 6px 4px;
  }
}

.peers-table th {
  background-color: #e0f5ff;
  color: #1c1c1c;
  font-weight: 600;
  text-align: left;
  padding: 10px 15px;
  font-size: 14px;
  font-family: "Figtree";
  text-transform: capitalize;
}

.peers-table td {
  padding: 10px 15px;
  border-bottom: 1px solid #e0f5ff;
  font-size: 14px;
  color: #1c1c1c;
  text-align: left;
  font-family: "Figtree";
  text-transform: capitalize;
  min-width: 102px;
}

th {
  border: none;
  border-bottom: 1px solid #22409633;
}

.loader-wrapper {
  top: 0;
  height: 100vh;
  width: 100vw;
  z-index: 1000;
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 8%);
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
}

/* ------------------------------------------------------------- */
.map-container {
  width: 100%;
  height: 310px;
  /* Adjust height as needed */
  min-height: 210px;
  position: relative;
}

.headquaters-heading {
  margin: 0;
  letter-spacing: 0.25px;
}

.headquaters-container {
  align-self: stretch;
  position: relative;
  font-size: 14px;
  line-height: 20px;
  color: #3e4855;
  letter-spacing: 0.25px;
}

.card-base-header {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 24px;
  gap: 6px;
  line-height: 18px;
  letter-spacing: 0.25px;
}

.finance-currency {
  right: 24px;
  position: absolute;
  color: #142d4d;
  font-weight: 600;
}

.map-base-header {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 24px 24px 0px 24px;
  gap: 6px;
}

.chart-base {
  flex: 1;
  width: 100%;
  position: relative;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  background-color: #fff;
  border: 1px solid #e2e8f0;
  box-sizing: border-box;
  height: 450px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  text-align: left;
  font-size: 16px;
  color: #04162d;
}

.line-charts {
  flex: 1;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  background-color: #fff;
  border: 1px solid #e2e8f0;
  box-sizing: border-box;
  height: 412px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  z-index: 0;
}

.chart-div {
  width: 100%;
  position: relative;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  align-items: flex-start;
  justify-content: flex-start;
  gap: 24px;
  text-align: left;
  font-size: 16px;
  color: #04162d;
}

.heading-3-line-chart-parent {
  flex: 1;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  background-color: #fff;
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 24px;
  gap: 24px;
}

.type {
  align-self: stretch;
  position: relative;
  line-height: 130%;
  font-weight: 500;
}

.subsidiary {
  align-self: stretch;
  position: relative;
  line-height: 130%;
}

.cells3 {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: 16px 24px;
}

.cells5 {
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  padding: 16px 24px;
  gap: 6px;
}

.table {
  align-self: stretch;
  border-radius: 8px;
  background-color: #fff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  font-size: 14px;
  color: #3e4855;
}

.ratio {
  align-self: stretch;
  position: relative;
  line-height: 130%;
  font-weight: 500;
}

.rows1 {
  width: 100%;
  position: relative;
  border-bottom: 1px solid #e2e8f0;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  text-align: left;
  font-size: 14px;
  color: #3e4855;
}

.incorporation-date {
  align-self: stretch;
  position: relative;
  line-height: 130%;
  font-weight: 500;
}

.cells1 {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: 16px 24px;
  color: #3e4855;
}

.table-value {
  align-self: stretch;
  position: relative;
  line-height: 130%;
  text-align: left;
}

.heading-3 {
  width: 100%;
  position: relative;
  font-size: 16px;
  line-height: 100%;
  font-weight: 600;
  color: #04162d;
  text-align: left;
  display: inline-block;
  font-family: "Figtree";
}

.cells {
  flex: 1;
  /* background-color: #e0f5ff; */
  /* border-bottom: 1px solid #e0f5ff; */
  box-sizing: border-box;
  height: 50px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: 16px 24px;
  text-align: left;
  text-transform: capitalize;
  border-collapse: collapse;
}

.purchase {
  text-decoration: underline;
}

.report-to-view {
  color: #3e4855;
}

.purchase-report-to-container {
  align-self: stretch;
  position: relative;
  line-height: 130%;
}

.rows {
  align-self: stretch;
  border-bottom: 1px solid #e2e8f0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  align-items: center;
  justify-content: center;
  color: #04162d;
}

.total-shares {
  align-self: stretch;
  position: relative;
  line-height: 130%;
  font-weight: 500;
}

.footer-heading {
  align-self: stretch;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 8px 24px;
  gap: 16px;
  position: relative;
  font-size: 30px;
  letter-spacing: 0.25px;
  line-height: 36px;
  color: #04162d;
}

.as-of{
  font-size: 13px;
  font-family: "Figtree";
  margin:0;
  line-height:15px;
}

.header {
  align-self: stretch;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  padding: 0px;
  gap: 16px;
  position: relative;
  font-size: 30px;
  letter-spacing: 0.25px;
  line-height: 36px;
  color: #04162d;
}

.number {
  position: relative;
  font-size: 24px;
  letter-spacing: -0.6px;
  line-height: 100%;
  font-weight: 600;
  font-family: "Figtree";
  color: #04162d;
  text-align: left;
}

.percent {
  flex: 1;
  position: relative;
  line-height: 20px;
}

.caption {
  align-self: stretch;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  /* padding: 0px 24px; */
  gap: 4px;
  font-size: 13px;
  color: #3e4855;
}

.footer-caption {
  align-self: stretch;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  font-size: 14px;
  color: #3e4855;
}

.left-part {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 8px;
}

.button1 {
  border-radius: 6px;
  background-color: #fff;
  display: inline-flex;
  /* Keeps button width dynamic */
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  box-sizing: border-box;
  width: fit-content;
  /* Ensures it fits text width */
  min-width: unset;
  max-width: 100%;
  /* Prevents it from overflowing */
  color: #04162d;
  position: relative;
  font-weight: 500;
  white-space: nowrap;
  /* Prevents text from wrapping */
  overflow: visible;
  /* Ensures full content visibility */
  text-align: center;
  border: none;
}

.lucide-icons {
  width: 16px;
  position: relative;
  height: 16px;
  overflow: hidden;
  flex-shrink: 0;
}

.button2 {
  cursor: pointer;
  border-radius: 12px;
  background: #fd4c8c;
  box-shadow: 1px 0px 5px #fd4c8c;
  color: #f8fafc;
  display: inline-flex;
  /* Keeps button width dynamic */
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  box-sizing: border-box;
  width: fit-content;
  /* Ensures it fits text width */
  min-width: unset;
  max-width: 100%;
  /* Prevents it from overflowing */
  position: relative;
  font-weight: 500;
  white-space: nowrap;
  /* Prevents text from wrapping */
  overflow: visible;
  /* Ensures full content visibility */
  text-align: center;
  border: none;
}

.buttons-group {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 12px;
}

.most-recent-financial {
  margin: 0;
  font-weight: 500;
}

.caption2 {
  position: relative;
  font-size: 13px;
  line-height: 16px;
  color: #3e4855;
  text-align: right;
}

.right-part {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-start;
  gap: 12px;
  font-size: 14px;
  color: #0f172a;
}

.title {
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0px;
  text-align: left;
  font-size: 18px;
  font-weight: 600;
  color: #04162d;
}

.tile {
  width: 100%;
  position: relative;
  border-radius: 8px;
  background-color: #fff;
  border: 1.5px solid #e0f5ff;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  text-align: left;
  font-size: 14px;
  color: #3e4855;
}

.content {
  background-color: #eef7ff;
  flex: 1;
  width: 100%;
  position: relative;
  border-radius: 0px 0px 8px 8px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 24px;
  gap: 16px;
  text-align: left;
  font-size: 16px;
  color: #04162d;
  letter-spacing: 0.25px;
  padding-top: 20px;
}

.content1 {
  flex: 1;
  width: 100%;
  position: relative;
  border-radius: 0px 0px 8px 8px;
  background-color: #fff;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 16px 24px;
  gap: 16px;
  text-align: left;
  font-size: 16px;
  color: #04162d;
}

@media only screen and (min-width: 1600px) {
  .blur-overlay {
    left: calc(50% - 220px);
  }
}

@media only screen and (max-width: 580px) {
  .blur-overlay {
    left: calc(50% - 90px);
  }
}

/* Mobile screens (max-width: 767px) */
@media only screen and (max-width: 767px) {
  html,
  body {
    overflow-x: hidden;
    /* Prevent horizontal scrolling */
    max-width: 100%;
    width: 100%;
    box-sizing: border-box;
  }

  .card-base-header,
  .map-base-header,
  .chart-base,
  .line-charts,
  .heading-3-line-chart-parent,
  .table {
    padding: 16px;
    gap: 12px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  .caption {
    padding: 0px 0px;
    width: 100%;
    max-width: 100%;
  }

  .chart-div {
    flex-direction: column;
    gap: 16px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    grid-template-columns: repeat(1, 1fr);
  }

  .header {
    font-size: 24px;
    line-height: 32px;
    padding: 8px 0px;
    width: 100%;
    max-width: 100%;
  }

  .footer-heading {
    font-size: 24px;
    line-height: 32px;
    padding: 8px 0px;
    width: 100%;
    max-width: 100%;
  }

  .number {
    font-size: 20px;
  }

  .percent {
    font-size: 14px;
  }

  .cells,
  .cells1,
  .cells3,
  .cells5 {
    padding: 12px 16px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  .buttons-group {
    flex-direction: row;
    flex-wrap: wrap;
    /* Prevents buttons from overflowing */
    gap: 15px;
    max-width: 100%;
  }

  .button1,
  .button2 {
    width: 100%;
    padding: 10px;
    box-sizing: border-box;
    max-width: 100%;
  }

  .title {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    line-height: normal;
    width: 100%;
    max-width: 100%;
    font-size: 16px;
  }

  .right-part {
    align-items: flex-start;
    width: 100%;
    max-width: 100%;
  }

  .caption2 {
    display: flex;
    flex-direction: column;
    gap: 3px;
    width: 100%;
    max-width: 100%;
    text-align: left;
  }

  .tile,
  .content {
    padding: 16px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  .content1 {
    padding: 0px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  .table {
    padding: 0px;
  }

  .card-base-header,
  .map-base-header {
    padding: 0px;
  }

  .lucide-icons {
    max-width: 16px;
    /* Ensures icons don't overflow */
    height: auto;
  }
}

/* Tablets (768px - 1023px) */
@media only screen and (min-width: 768px) and (max-width: 1023px) {
  html,
  body {
    overflow-x: hidden;
    /* Prevent horizontal scrolling */
    max-width: 100%;
  }

  .chart-div {
    flex-direction: column;
    gap: 16px;
    width: 100%;
    /* Ensure it doesn’t exceed the viewport */
    max-width: 100%;
    box-sizing: border-box;
    flex-wrap: wrap;
    /* Allows wrapping if necessary */
  }

  .card-base-header,
  .map-base-header,
  .chart-base,
  .line-charts,
  .heading-3-line-chart-parent,
  .table {
    padding: 20px;
    gap: 10px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  .card-base-header,
  .map-base-header,
  .table {
    padding: 0px;
  }

  .header {
    font-size: 28px;
    line-height: 34px;
    width: 100%;
    padding: 12px 0px;
  }

  .footer-heading {
    font-size: 28px;
    line-height: 34px;
    width: 100%;
    padding: 12px 0px;
  }

  .number {
    font-size: 22px;
  }

  .percent {
    font-size: 14px;
  }

  .cells,
  .cells1,
  .cells3,
  .cells5 {
    padding: 14px 20px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  .buttons-group {
    flex-direction: row;
    flex-wrap: wrap;
    /* Allows buttons to wrap instead of overflowing */
    gap: 15px;
    width: 100%;
  }

  .caption {
    padding: 0px 0px;
  }

  .caption2 {
    display: flex;
    flex-direction: row;
    gap: 8px;
    width: 100%;
    max-width: 100%;
  }

  .button1,
  .button2 {
    padding: 8px 12px;
    max-width: 100%;
    box-sizing: border-box;
  }

  .title {
    flex-direction: column;
    justify-content: space-between;
    width: 100%;
    max-width: 100%;
    line-height: normal;
    gap: 10px;
  }

  .right-part {
    align-self: stretch;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 8px;
  }

  .tile {
    padding: 20px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  .content {
    padding: 20px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  .content1 {
    padding: 0px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }
}
</style>
